import { useState, useRef } from "react";
import {
  Clock,
  FileCode2,
  GitCommit,
  ArrowRight,
  Pencil,
  Check,
  ExternalLink,
  Copy,
  Flame,
} from "lucide-react";
import { fmtDuration, fmtRelative, basename } from "../lib/format";
import { updateSessionNote } from "../lib/api";
import { track } from "../lib/track";
import { DEMO_NOTE } from "../lib/demo";
import { toast } from "sonner";
import TraceAiInsight from "./TraceAiInsight";

export default function ResumeSessionCard({
  project,
  session,
  onNoteSaved,
  onResume,
  onViewTimeline,
  demo = false,
}) {
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState(session?.session_note || "");
  const [saving, setSaving] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [rippling, setRippling] = useState(false);
  const cardRef = useRef(null);

  if (!session) {
    return (
      <div
        data-testid="resume-empty"
        className="trace-card p-10 trace-fade-in flex flex-col items-start gap-3"
      >
        <span className="text-xs uppercase tracking-wider text-[#4b5563]">Resume Session</span>
        <h2 className="text-2xl tracking-tight text-[#111827]">
          We're listening. Sync this project to remember how you built it.
        </h2>
        <p className="text-sm text-[#4b5563] max-w-lg leading-relaxed">
          As soon as you commit code, Trace groups your activity into sessions and remembers
          where you left off — so you never lose project context again.
        </p>
      </div>
    );
  }

  const files = session.files_touched || [];
  const ranked = session.files_ranked || files;
  const realNote = (session.session_note || "").trim();
  // In demo mode: lock hero to the session topic ("Auth flow") and surface
  // a clean seeded note so the camera never lands on an empty placeholder.
  const displayNote = demo && !realNote ? DEMO_NOTE : realNote;
  const hero = demo
    ? session.topic || displayNote || "this project"
    : displayNote || session.topic || "this project";
  const ctaLabel = `Resume ${hero}`;

  const primaryFile = session.primary_file || ranked[0] || files[0];
  const primaryFileUrl =
    primaryFile && project?.repo_url
      ? `${project.repo_url}/blob/${project.default_branch || "main"}/${primaryFile}`
      : project?.repo_url;

  const onSave = async () => {
    setSaving(true);
    try {
      await updateSessionNote(project.id, session.id, note);
      toast.success("Saved");
      setEditing(false);
      onNoteSaved?.();
    } catch {
      toast.error("Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  const onCopyStandup = async () => {
    const yesterdayBase =
      aiSummary?.building || session.topic || "Continued work on the project";
    const standupNote = displayNote;
    const yesterday = standupNote
      ? `${yesterdayBase} (note: ${standupNote})`
      : yesterdayBase;
    const today =
      aiSummary?.next_step || session.suggested_next_step || "Continue where I left off";
    const md = `- Yesterday: ${yesterday}\n- Today: ${today}\n- Blockers: —`;
    try {
      await navigator.clipboard.writeText(md);
      track("Standup Copied");
      toast.success("Copied — paste into Slack at standup.");
    } catch {
      toast.error("Could not copy. Select & copy manually.");
    }
  };

  return (
    <div
      ref={cardRef}
      data-testid="resume-card"
      className={`relative trace-card p-5 sm:p-10 trace-fade-in overflow-hidden transition-shadow duration-500 ${
        rippling ? "trace-ripple" : ""
      }`}
    >
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-[0.06] bg-gradient-to-br from-blue-500 to-indigo-500 blur-3xl pointer-events-none" />

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[#4b5563] font-medium">
          Resume Session
        </span>
        <span className="w-1 h-1 rounded-full bg-[#9ca3af]" />
        <span className="text-[11px] text-[#6b7280]">
          last active {fmtRelative(session.end_time)}
        </span>
      </div>

      {/* Hero */}
      <div>
        <div className="text-[12px] text-[#4b5563] mb-1 font-medium">You were working on:</div>
        <h1 className="text-3xl sm:text-[42px] tracking-tight text-[#0b1220] leading-[1.05] font-semibold max-w-3xl">
          <span className="gradient-text">{hero}</span>
        </h1>
      </div>

      {/* Trace Insight */}
      <TraceAiInsight
        projectId={project.id}
        sessionId={session.id}
        eligible={
          (session.files_touched?.length || 0) >= 2 ||
          (session.commit_shas?.length || 0) >= 1
        }
        onLoaded={setAiSummary}
      />

      {/* Files touched strip */}
      {files.length > 0 && (
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.15em] text-[#4b5563] font-semibold">
            Files touched
          </div>
          <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1.5" data-testid="resume-files">
            {ranked.slice(0, 8).map((f, i) => (
              <li key={f} className="flex items-center gap-1.5">
                {i > 0 && <span className="text-[#cbd5e1]">·</span>}
                <span
                  className={`font-mono text-[12.5px] ${
                    f === primaryFile ? "text-[#0b1220] font-semibold" : "text-[#1f2937]"
                  }`}
                  title={
                    f === primaryFile && session.primary_is_dominant
                      ? `${f} — most edited file in this session`
                      : f
                  }
                >
                  {basename(f)}
                </span>
                {f === primaryFile && session.primary_is_dominant && (
                  <Flame
                    size={11}
                    strokeWidth={2.4}
                    className="text-rose-500"
                    aria-label="dominant file"
                    data-testid="primary-flame"
                  />
                )}
              </li>
            ))}
            {ranked.length > 8 && (
              <li className="text-[11px] text-[#6b7280]">+{ranked.length - 8} more</li>
            )}
          </ul>
        </div>
      )}

      {/* Tertiary stats */}
      <div className="mt-5 flex items-center gap-6 text-[11px] text-[#4b5563] font-medium">
        <span className="flex items-center gap-1.5">
          <Clock size={12} strokeWidth={2.2} /> {fmtDuration(session.duration_seconds)}
        </span>
        <span className="flex items-center gap-1.5">
          <FileCode2 size={12} strokeWidth={2.2} /> {session.files_touched_count} files
        </span>
        <span className="flex items-center gap-1.5">
          <GitCommit size={12} strokeWidth={2.2} /> {(session.commit_shas || []).length} commits
        </span>
      </div>

      {/* Note prompt */}
      <div className="mt-6 rounded-xl bg-white border border-[#e5e7eb] p-4">
        <div className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wider text-[#4b5563] font-semibold">
            What were you trying to finish here?
          </span>
          {!editing ? (
            <button
              data-testid="note-edit-btn"
              onClick={() => setEditing(true)}
              className="text-[#4b5563] hover:text-[#111827] transition"
            >
              <Pencil size={14} strokeWidth={2.2} />
            </button>
          ) : (
            <button
              data-testid="note-save-btn"
              disabled={saving}
              onClick={onSave}
              className="text-indigo-600 hover:text-indigo-700 transition disabled:opacity-60 flex items-center gap-1 text-xs font-medium"
            >
              <Check size={14} strokeWidth={2.4} /> Save
            </button>
          )}
        </div>
        {editing ? (
          <textarea
            data-testid="note-input"
            autoFocus
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Wiring the auth modal close handler"
            className="mt-2 w-full text-sm bg-[#f7f8fa] border border-[#e5e7eb] rounded-md px-3 py-2 outline-none focus:border-indigo-400 transition text-[#111827]"
          />
        ) : (
          <p className="mt-1.5 text-sm text-[#0b1220]">
            {displayNote ? (
              displayNote
            ) : (
              <span className="text-[#6b7280] italic">
                One line so future-you knows the intent.
              </span>
            )}
          </p>
        )}
      </div>

      {/* CTA row */}
      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <button
          data-testid="resume-work-btn"
          onClick={() => {
            track("Resume Session", { props: { project: project?.name, demo } });
            if (demo) {
              // Stay inside Trace — trigger the ripple effect on the card.
              setRippling(false);
              // restart animation by toggling on next frame
              requestAnimationFrame(() => {
                setRippling(true);
                setTimeout(() => setRippling(false), 1200);
              });
              toast.success(`Picked up ${hero}.`, { duration: 2200 });
              onResume?.(session);
              return;
            }
            if (primaryFileUrl) {
              window.open(primaryFileUrl, "_blank", "noopener,noreferrer");
            }
            onResume?.(session);
          }}
          className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(99,102,241,0.4)] transition-all duration-300"
          title={primaryFile ? `Opens ${primaryFile}` : "Open session"}
        >
          {ctaLabel}
          <ArrowRight
            size={14}
            strokeWidth={2.4}
            className="transition-transform duration-300 group-hover:translate-x-0.5"
          />
        </button>

        <button
          data-testid="copy-standup-btn"
          onClick={onCopyStandup}
          className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-[12.5px] font-medium text-[#1f2937] bg-white border border-[#e5e7eb] hover:bg-[#f7f8fa] hover:-translate-y-0.5 hover:shadow-sm transition-all duration-300"
          title="Copy a 3-line standup-ready markdown to your clipboard"
        >
          <Copy size={13} strokeWidth={2.2} />
          Copy as standup
        </button>

        {primaryFile && !demo && (
          <span className="text-[11px] text-[#4b5563] font-mono inline-flex items-center gap-1">
            <ExternalLink size={11} strokeWidth={2.2} /> opens {basename(primaryFile)}
          </span>
        )}
        {onViewTimeline && (
          <button
            data-testid="view-timeline-btn"
            onClick={onViewTimeline}
            className="ml-auto text-sm text-[#4b5563] hover:text-[#111827] transition font-medium"
          >
            View full timeline →
          </button>
        )}
      </div>
    </div>
  );
}
